import { CustomStaticTableProps } from "./types";
import {
	TableBody,
	TableRow,
	Table,
	TableHeader,
	TableHeaderCell,
	useTableFeatures,
	TableColumnId,
	useTableSort,
	mergeClasses,
} from "@fluentui/react-components";

import { useCallback, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useTableStyle } from "./style";
import { captureError } from "@/common/helpers/sentryHelper";

export const CustomStaticTable = (props: CustomStaticTableProps) => {
	const { columnInfo, columnDefinitions, getRowTemplateCallback } =
		props;
	let { items } =
		props;

	const logItemsTypeErrorOnSentry = (tableItems: any) => {
		try {
			const errorItemsType = new Error("useTableFeatures is expecting an array for items");

			captureError(errorItemsType, "useTableFeatures map error", tableItems)
		} catch (error: any) {
			captureError(error, "CustomStaticTable.tryLogSentryError failed");
		}
	}

	if (!Array.isArray(items)) {
		logItemsTypeErrorOnSentry(items);
		items = [];
	}

	const intl = useIntl();
	const [sortState, setSortState] = useState<{
		sortDirection: "ascending" | "descending";
		sortColumn: TableColumnId | undefined;
	}>({
		sortDirection: "ascending" as const,
		sortColumn: "Customer",
	});
	const classes = useTableStyle();

	const {
		getRows,
		//sort: { getSortDirection },
	} = useTableFeatures(
		{
			columns: columnDefinitions,
			items,
		},
		[
			useTableSort({
				sortState,
				onSortChange: (_, nextSortState) => setSortState(nextSortState),
			}),
		]
	);

	const onSortChange = (
		_: React.MouseEvent,
		columnId: TableColumnId | undefined
	) => {
		const sortDirection =
			sortState.sortDirection !== "ascending" ? "ascending" : "descending";

		setSortState({ sortDirection: sortDirection, sortColumn: columnId });
	};

	const headerSortProps = (columnId: TableColumnId) => ({
		onClick: (e: React.MouseEvent) => onSortChange(e, columnId),
		sortDirection: undefined, //use getSortDirection(columnId) if you need sorting
	});

	const interactionTableColumnName = useCallback(
		(name: string, label?: string) =>
			name.length
				? label ?? intl.formatMessage({ id: `Interaction.Table.${name}` })
				: "",
		[intl]
	);

	const rows = getRows();

	const header = useMemo<JSX.Element[]>(() => {
		return columnInfo.map((column) => (
			<TableHeaderCell
				key={column.name}
				style={{
					width: column.width,
					paddingLeft: column.leftOffset ?? "8px",
				}}
				{...headerSortProps(column.name)}
				className={mergeClasses(
					props.isStickyHeader ? classes.stickyHeader : ""
				)}
			>
				{interactionTableColumnName(column.name, column.label)}
			</TableHeaderCell>
		));
	}, [sortState, columnInfo]);

	const body = useMemo<JSX.Element[]>(
		() =>
			getRowTemplateCallback
				? rows.map(({ item }) => getRowTemplateCallback(item))
				: [],
		[rows, getRowTemplateCallback]
	);

	return (
		<Table
			sortable={props.sortable == null || props.sortable}
			aria-label="custom-table"
			className={classes.table}
		>
			{!props.hideHeaders && (
				<TableHeader className={classes.table}>
					<TableRow className={mergeClasses(classes.row, classes.headerText)}>
						{header}
					</TableRow>
				</TableHeader>
			)}
			{getRowTemplateCallback && <TableBody>{body}</TableBody>}
		</Table>
	);
};
